import { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { Menu, X, User, LogIn } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import type { User as SupabaseUser } from "@supabase/supabase-js";

export const Navbar = () => {
  const { t, language, setLanguage } = useLanguage();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const location = useLocation();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);
  
  // Dynamic text color based on route - editorial pages (Desk + Contact) get brighter text
  const isEditorialPage = useMemo(() => {
    const editorialPages = ['/quick-picks', '/contact'];
    return editorialPages.some(page => location.pathname.startsWith(page));
  }, [location.pathname]);
  
  // Editorial pages: brighter white text for readability on pure black
  const navTextColor = isEditorialPage ? "text-white/70" : "text-white/60";
  const navTextHover = "hover:text-white";

  return (
    <>
      <motion.nav
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
        className="fixed top-0 left-0 right-0 z-[100]"
      >
        {/* Pure transparent - no backgrounds, no blur */}
        <div className="container mx-auto px-6 py-6 flex items-center justify-between">
          {/* Logo - Times New Roman, editorial */}
          <Link 
            to="/" 
            className={`font-serif text-[11px] tracking-[0.35em] uppercase ${navTextColor} transition-all duration-500 ${navTextHover}`}
          >
            ALABAMA <span className="italic opacity-50">Ai</span>
          </Link>

          {/* Desktop Navigation - text only, wide tracking */}
          <div className="hidden md:flex items-center gap-10">
            <Link to="/" className={`font-serif text-[10px] tracking-[0.35em] uppercase ${navTextColor} ${navTextHover} transition-all duration-500`}>
              {t("nav.home")}
            </Link>
            <Link to="/about" className={`font-serif text-[10px] tracking-[0.35em] uppercase ${navTextColor} ${navTextHover} transition-all duration-500`}>
              {t("nav.about")}
            </Link>
            <Link to="/work" className={`font-serif text-[10px] tracking-[0.35em] uppercase ${navTextColor} ${navTextHover} transition-all duration-500`}>
              {t("nav.work")}
            </Link>
            <Link to="/quick-picks" className={`font-serif text-[10px] tracking-[0.35em] uppercase ${navTextColor} ${navTextHover} transition-all duration-500`}>
              {t("nav.desk")}
            </Link>
            <Link to="/generate" className={`font-serif text-[10px] tracking-[0.35em] uppercase ${navTextColor} ${navTextHover} transition-all duration-500`}>
              {t("nav.generate")}
            </Link>
            <Link to="/contact" className={`font-serif text-[10px] tracking-[0.35em] uppercase ${navTextColor} ${navTextHover} transition-all duration-500`}>
              {t("nav.contact")}
            </Link>
          </div>

          {/* Right Side - language + cart */}
          <div className="flex items-center gap-6">
            {/* Language Toggle */}
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={() => setLanguage("en")}
                className={`font-serif text-[10px] tracking-[0.25em] transition-all duration-500 ${
                  language === "en" ? "text-white" : `${navTextColor} ${navTextHover}`
                }`}
              >
                EN
              </button>
              <span className="text-white/20">/</span>
              <button
                onClick={() => setLanguage("es")}
                className={`font-serif text-[10px] tracking-[0.25em] transition-all duration-500 ${
                  language === "es" ? "text-white" : `${navTextColor} ${navTextHover}`
                }`}
              >
                ES
              </button>
            </div>

            {/* PlayBack Auth Actions */}
            {user ? (
              <Link 
                to="/playback/dashboard"
                className={`flex items-center gap-2 ${navTextColor} transition-all duration-500 ${navTextHover}`}
              >
                <User className="h-4 w-4" />
                <span className="font-serif text-[10px] tracking-[0.25em] uppercase">PlayBack</span>
              </Link>
            ) : (
              <div className="flex items-center gap-4">
                <Link
                  to="/playback/login"
                  className={`font-serif text-[10px] tracking-[0.25em] uppercase ${navTextColor} ${navTextHover} transition-all duration-500`}
                >
                  Log in
                </Link>
                <Link
                  to="/playback/signup"
                  className="font-serif text-[10px] tracking-[0.25em] uppercase px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded transition-all duration-500"
                >
                  Sign up
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className={`md:hidden ${navTextColor} ${navTextHover} transition-all duration-500`}
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu Drawer */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100]"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="fixed left-0 top-0 h-full w-80 bg-black border-r border-white/10 z-[100] p-8"
            >
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="absolute top-6 right-6 text-white/40 hover:text-white transition-all duration-500"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>

              <div className="flex flex-col gap-8 mt-20">
                <Link
                  to="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="font-serif text-[11px] tracking-[0.35em] uppercase text-white/50 hover:text-white transition-all duration-500"
                >
                  {t("nav.home")}
                </Link>
                <Link
                  to="/about"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="font-serif text-[11px] tracking-[0.35em] uppercase text-white/50 hover:text-white transition-all duration-500"
                >
                  {t("nav.about")}
                </Link>
                <Link
                  to="/work"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="font-serif text-[11px] tracking-[0.35em] uppercase text-white/50 hover:text-white transition-all duration-500"
                >
                  {t("nav.work")}
                </Link>
                <Link
                  to="/quick-picks"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="font-serif text-[11px] tracking-[0.35em] uppercase text-white/50 hover:text-white transition-all duration-500"
                >
                  {t("nav.desk")}
                </Link>
                <Link
                  to="/generate"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="font-serif text-[11px] tracking-[0.35em] uppercase text-white/50 hover:text-white transition-all duration-500"
                >
                  {t("nav.generate")}
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="font-serif text-[11px] tracking-[0.35em] uppercase text-white/50 hover:text-white transition-all duration-500"
                >
                  {t("nav.contact")}
                </Link>

                <div className="flex items-center gap-4 mt-6 pt-6 border-t border-white/10">
                  <button
                    onClick={() => setLanguage("en")}
                    className={`font-serif text-[10px] tracking-[0.25em] transition-all duration-500 ${
                      language === "en" ? "text-white" : "text-white/40 hover:text-white"
                    }`}
                  >
                    EN
                  </button>
                  <span className="text-white/20">/</span>
                  <button
                    onClick={() => setLanguage("es")}
                    className={`font-serif text-[10px] tracking-[0.25em] transition-all duration-500 ${
                      language === "es" ? "text-white" : "text-white/40 hover:text-white"
                    }`}
                  >
                    ES
                  </button>
                </div>

                {user ? (
                  <Link
                    to="/playback/dashboard"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 text-white/50 hover:text-white transition-all duration-500"
                  >
                    <User className="h-4 w-4" />
                    <span className="font-serif text-[11px] tracking-[0.35em] uppercase">PlayBack</span>
                  </Link>
                ) : (
                  <Link
                    to="/playback/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 text-white/50 hover:text-white transition-all duration-500"
                  >
                    <LogIn className="h-4 w-4" />
                    <span className="font-serif text-[11px] tracking-[0.35em] uppercase">Log in</span>
                  </Link>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

    </>
  );
};